---
layout: docs
title: "The Hub"
---


The Hub is where your team interacts with AI. It's a web-based chat interface that connects users to the assistants and sub-assistants configured for your organization.

---

## What You See

When you log in to the Hub, you see:

- **Chat interface** — the main conversation area with your Core Assistant
- **Conversation history** — past conversations in the sidebar, continuable at any time
- **Assistant selector** — switch between available assistants
- **Workspace panel** — files in your persistent workspace, updated in real time as the assistant creates or modifies them
- **Canvas** — a visual workspace that appears alongside the conversation when the assistant generates charts, diagrams, code, or other rich content

---

## Conversations

Conversations with your assistant are persistent by default. You can leave and come back — your conversation history and context are preserved.

Start a new conversation at any time, or continue a previous one from the sidebar.

---

## Working with Files

Your assistant has a persistent file system. When it writes a file — a report, a data analysis, a code file — that file appears in your workspace panel and persists across conversations.

You can:
- See files the assistant creates in real time
- Upload files for the assistant to work with
- Reference files across conversations ("remember that report from yesterday")

Files in the `/workspace/` scope are persistent. Files in `/session/` last for the current conversation. Files in `/tmp/` are ephemeral.

---

## Code Execution

Your assistant can execute code in a sandboxed environment. When it runs code:

- The execution happens in an isolated container — completely separated from Corral's infrastructure and your Azure resources
- Files from your workspace are automatically staged into the execution environment
- Results (output, generated files) are returned to the conversation and workspace

This is useful for data analysis, file processing, calculations, and generating visualizations.

---

## The Canvas

The canvas is a visual workspace that appears alongside your conversation. It activates when the assistant generates visual content:

- **Charts** — data visualizations rendered from your data
- **Diagrams** — Mermaid diagrams for flowcharts, sequence diagrams, architecture diagrams
- **Generated UI** — on-the-fly interfaces the assistant creates
- **Code and documents** — rendered previews of files the assistant writes

The canvas uses a unified protocol for all generated visual content — everything renders in the same panel, regardless of type.

<!-- TODO: Specific canvas interaction capabilities — can you edit content in the canvas? Can you export? Can you pin canvas content? Screenshots of what the canvas looks like with different content types would be very helpful here. -->

*This section is a work in progress.*


---

## Sub-Assistants

From the Hub, you can create sub-assistants for domain-specific work:

- Ask your Core Assistant to create one ("create a marketing sub-assistant")
- Or create one directly from the Hub interface

Each sub-assistant gets its own workspace, files, and conversation history. Switch between your Core Assistant and sub-assistants from the sidebar.

See [Assistants & Sub-Assistants →](../building-agents/assistants-and-sub-assistants) for the full model.
